import React from 'react';
import {
  Box, Typography, Chip, TextField, Button, CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { UserActivityRow } from '../../types';

interface UsersActivityTableProps {
  usersActivity: UserActivityRow[];
  usersActivityLoading: boolean;
  activitySearch: string;
  setActivitySearch: (v: string) => void;
  selectedMonth: string;
  monthsList: { value: string; label: string }[];
  onRefresh: () => void;
}

/**
 * Таблица активности пользователей по аптекам за выбранный период.
 * Показывает количество записей и статус (Активен / Нет записей).
 * Пользователи без записей — красная подсветка строки, идут в начале списка.
 */
const UsersActivityTable: React.FC<UsersActivityTableProps> = ({
  usersActivity, usersActivityLoading,
  activitySearch, setActivitySearch,
  selectedMonth, monthsList, onRefresh,
}) => {
  const term = activitySearch.toLowerCase().trim();
  const filtered = usersActivity.filter(u =>
    !term ||
    u.username.toLowerCase().includes(term) ||
    u.pharmacyNum.includes(term)
  );
  const noRecordCount = filtered.filter(u => u.recordCount === 0).length;

  return (
    <Box>
      {/* Заголовок и поиск */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            Период: <strong>{monthsList.find(m => m.value === selectedMonth)?.label ?? selectedMonth}</strong>
            {' · '}{usersActivity.length} пользователей
            {' · '}<span style={{ color: '#ef4444' }}>{usersActivity.filter(u => u.recordCount === 0).length} без записей</span>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Поиск по имени или аптеке…"
            value={activitySearch}
            onChange={e => setActivitySearch(e.target.value)}
            sx={{ width: 260 }}
            InputProps={{ startAdornment: <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} /> }}
          />
          <Button
            size="small" variant="outlined" startIcon={<RefreshIcon />}
            onClick={onRefresh} disabled={usersActivityLoading}
          >
            Обновить
          </Button>
        </Box>
      </Box>

      {usersActivityLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
          <CircularProgress size={36} />
          <Typography color="text.secondary">Загрузка данных…</Typography>
        </Box>
      ) : filtered.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={6}>Нет совпадений</Typography>
      ) : (
        <Box>
          {/* Сводка */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{
              px: 2, py: 1, borderRadius: 2,
              bgcolor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <CancelIcon sx={{ fontSize: 16, color: '#ef4444' }} />
              <Typography variant="body2" fontWeight={700} color="#ef4444">
                {noRecordCount} без записей
              </Typography>
            </Box>
            <Box sx={{
              px: 2, py: 1, borderRadius: 2,
              bgcolor: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)',
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
              <Typography variant="body2" fontWeight={700} color="#10b981">
                {filtered.length - noRecordCount} активных
              </Typography>
            </Box>
          </Box>

          {/* Заголовок таблицы */}
          <Box sx={{
            display: 'grid', gridTemplateColumns: '44px 1fr 90px 130px 140px',
            gap: 1, px: 2, py: 1.25,
            bgcolor: 'grey.50', borderRadius: '8px 8px 0 0',
            border: '1px solid', borderColor: 'divider', alignItems: 'center',
          }}>
            {['№', 'Пользователь', 'Аптека', 'Записей за месяц', 'Статус'].map((h, i) => (
              <Typography key={h} variant="caption" color="text.secondary" fontWeight={700}
                sx={{ textTransform: 'uppercase', fontSize: '0.7rem', textAlign: i >= 3 ? 'right' : undefined }}>
                {h}
              </Typography>
            ))}
          </Box>

          {/* Строки */}
          <Box sx={{
            border: '1px solid', borderTop: 0, borderColor: 'divider',
            borderRadius: '0 0 8px 8px', overflow: 'hidden',
            maxHeight: '60vh', overflowY: 'auto',
          }}>
            {filtered.map((u, idx) => {
              const isInactive = u.recordCount === 0;
              return (
                <Box
                  key={u.username}
                  sx={{
                    display: 'grid', gridTemplateColumns: '44px 1fr 90px 130px 140px',
                    gap: 1, px: 2, py: 1.25, alignItems: 'center',
                    borderBottom: '1px solid', borderColor: 'divider',
                    bgcolor: isInactive ? 'rgba(254,242,242,0.5)' : 'transparent',
                    '&:last-child': { borderBottom: 0 },
                    '&:hover': { bgcolor: isInactive ? 'rgba(254,242,242,0.8)' : 'rgba(99,102,241,0.03)' },
                  }}
                >
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>{idx + 1}</Typography>
                  <Typography variant="body2" fontWeight={isInactive ? 700 : 500} color={isInactive ? '#b91c1c' : 'text.primary'}>
                    {u.username}
                  </Typography>
                  <Chip
                    label={`№${u.pharmacyNum}`} size="small" variant="outlined"
                    sx={{ fontWeight: 700, fontSize: '0.72rem', borderRadius: '6px' }}
                  />
                  <Typography variant="body2" fontWeight={600} align="right" color={isInactive ? '#ef4444' : 'text.primary'}>
                    {isInactive ? '—' : u.recordCount.toLocaleString('ru-RU')}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip
                      icon={isInactive
                        ? <CancelIcon sx={{ fontSize: '14px !important' }} />
                        : <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                      }
                      label={isInactive ? 'Нет записей' : 'Активен'}
                      size="small"
                      sx={{
                        fontWeight: 700, fontSize: '0.72rem',
                        bgcolor: isInactive ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                        color: isInactive ? '#ef4444' : '#10b981',
                        border: '1px solid',
                        borderColor: isInactive ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)',
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UsersActivityTable;
